"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import dynamic from "next/dynamic"
import { useParams } from "next/navigation"

import { Category, FlashSaleCategory } from "@/lib/types"

// Skeleton components for loading states
const MenuTabsSkeleton = () => (
  <div className="sticky top-14 sm:top-16 bg-white z-[5] border-b border-gray-100 pb-3">
    <div className="flex gap-3 sm:gap-4 py-3">
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={index}
          className="h-7 sm:h-8 w-20 sm:w-28 flex-shrink-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md"
        />
      ))}
    </div>
  </div>
)

const MenuSectionsSkeleton = () => (
  <div className="mt-6">
    {Array.from({ length: 3 }).map((_, categoryIndex) => (
      <div key={categoryIndex} className="mb-8 sm:mb-10">
        <div className="mb-3 sm:mb-4 h-5 sm:h-6 w-36 sm:w-48 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, itemIndex) => (
            <div
              key={itemIndex}
              className="flex flex-row xl:flex-row md:flex-row lg:flex-col border border-gray-200 rounded-lg overflow-hidden w-full"
            >
              <div className="relative shrink-0 w-full xl:h-full xl:w-auto lg:w-full lg:max-w-full aspect-[16/9] xl:aspect-square min-h-40 max-w-40 xl:min-w-40 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer" />
              <div className="flex flex-col p-3 flex-grow">
                <div className="h-4 sm:h-5 w-32 sm:max-w-xs bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md" />
                <div className="h-3 sm:h-4 w-36 sm:max-w-sm bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md mt-2" />
                <div className="flex items-center justify-between mt-auto pt-2">
                  <div className="h-4 sm:h-5 w-16 sm:w-24 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md" />
                  <div className="h-6 w-6 rounded-md bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

const SearchSkeleton = () => (
  <div className="h-10 sm:h-12 w-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer rounded-md mb-4" />
)

// Lazy load heavy components with matching skeletons
const MenuCategoryTabs = dynamic(
  () => import("./menu-category-tabs").then((mod) => mod.MenuCategoryTabs),
  { ssr: true, loading: () => <MenuTabsSkeleton /> }
)

const MenuCategorySections = dynamic(
  () =>
    import("./menu-category-sections").then((mod) => mod.MenuCategorySections),
  { ssr: true, loading: () => <MenuSectionsSkeleton /> }
)

const MenuItemDetailDialog = dynamic(
  () =>
    import("./menu-item-detail-dialog").then((mod) => mod.MenuItemDetailDialog),
  { ssr: false, loading: () => null } // Dialog doesn't need a skeleton as it's not visible until clicked
)

const MenuSearch = dynamic(
  () => import("./menu-search").then((mod) => mod.MenuSearch),
  { ssr: true, loading: () => <SearchSkeleton /> }
)

interface MenuCategoriesProps {
  categories: Category[]
  flashSaleCategories?: FlashSaleCategory[]
  isClosed?: boolean
}

const MenuCategories = function MenuCategories({
  categories,
  flashSaleCategories,
  isClosed,
}: MenuCategoriesProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(
    categories.length > 0 ? String(categories[0].category_id) : null
  )
  const [isManualScrolling, setIsManualScrolling] = useState(false)
  const [filteredCategories, setFilteredCategories] = useState<
    Category[] | null
  >(null)
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null)
  const params = useParams()
  const restaurantId = params.id as string

  const processedCategories = useMemo(() => {
    let combinedCategories = [
      ...categories.map((c) => ({ ...c, category_id: c.category_id })),
    ] // Ensure all original category_ids are strings

    if (flashSaleCategories && flashSaleCategories.length > 0) {
      const validFlashSaleCategories = flashSaleCategories.reduce(
        (acc, fsCategory) => {
          if (fsCategory && fsCategory.items && fsCategory.items.length > 0) {
            const validItems = fsCategory.items.filter(
              (item) => item && item.item_name
            )
            if (validItems.length > 0) {
              acc.push({
                ...fsCategory,
                items: validItems,
                is_flash_sale: true,
                category_id: fsCategory.items.length, // Ensure flash sale category_id is a string
              })
            }
          }
          return acc
        },
        [] as Category[]
      )

      combinedCategories = [
        ...validFlashSaleCategories,
        ...categories.map((c) => ({
          ...c,
          category_id: c.category_id,
        })),
      ]
    }
    return combinedCategories
  }, [categories, flashSaleCategories])

  const displayCategories = useMemo(
    () => filteredCategories || processedCategories,
    [filteredCategories, processedCategories]
  )

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null
    return displayCategories
      .flatMap((cat) => cat.items)
      .find((item) => item.restaurant_item_id === selectedItemId)
  }, [selectedItemId, displayCategories])

  // Effect to update activeCategory if it's not in the current displayCategories
  useEffect(() => {
    if (displayCategories.length > 0) {
      const currentActiveExists = displayCategories.some(
        (cat) => String(cat.category_id) === activeCategory
      )
      if (!currentActiveExists) {
        setActiveCategory(String(displayCategories[0].category_id))
      }
    } else if (displayCategories.length === 0 && activeCategory !== null) {
      setActiveCategory(null) // No categories, no active
    }
  }, [displayCategories, activeCategory, setActiveCategory])

  // Handle click on a tab with useCallback
  const handleTabClick = useCallback((categoryId: string) => {
    // First update the active category to move the indicator
    setActiveCategory(categoryId)
    setIsManualScrolling(true)

    // Short delay before scrolling to ensure the indicator moves first
    setTimeout(() => {
      // Scroll to the selected section with smooth behavior
      const section = document.getElementById(`category-${categoryId}`)
      if (section) {
        // Calculate the offset including the header and tabs height
        const headerHeight = 64 // 16px for header (h-16)
        const tabsHeight = 48 // Approximate height of the tabs bar
        const offset = headerHeight + tabsHeight + 16 // Add some padding

        const top =
          section.getBoundingClientRect().top + window.scrollY - offset

        window.scrollTo({
          top,
          behavior: "smooth",
        })

        // Reset the manual scrolling flag after animation is likely complete
        setTimeout(() => {
          setIsManualScrolling(false)
        }, 800) // Slightly shorter time
      }
    }, 50) // Small delay to let the indicator move first
  }, [])

  // Handle section in view from intersection observer with useCallback
  const handleSectionInView = useCallback(
    (categoryId: string) => {
      if (!isManualScrolling) {
        setActiveCategory(categoryId)
      }
    },
    [isManualScrolling]
  )

  // Handle search results with useCallback
  const handleSearchResults = useCallback((results: Category[] | null) => {
    setFilteredCategories(results)
  }, [])

  const isFiltered = !!filteredCategories

  // Memoize the handler for selecting items
  const handleSelectItem = useCallback((itemId: number) => {
    setSelectedItemId(itemId)
  }, [])

  if (displayCategories.length === 0 && !isFiltered) {
    return (
      <div className="py-12 text-center">
        <p className="text-gray-500">Không có món ăn nào</p>
      </div>
    )
  }

  return (
    <section
      className="w-full"
      aria-labelledby="menu-heading"
      itemScope
      itemType="https://schema.org/Menu"
    >
      <h2 id="menu-heading" className="sr-only">
        Thực đơn nhà hàng
      </h2>
      <meta itemProp="name" content="Thực đơn nhà hàng" />

      {/* Search bar */}
      <MenuSearch
        categories={processedCategories}
        onSearchResults={handleSearchResults}
      />

      <div className="flex flex-col gap-4">
        <MenuCategoryTabs
          categories={displayCategories}
          activeCategory={activeCategory ?? ""}
          onCategoryChange={handleTabClick}
        />
        <div className="flex gap-4">
          <MenuCategorySections
            categories={displayCategories}
            activeCategory={activeCategory}
            isManualScrolling={isManualScrolling}
            onSectionInView={handleSectionInView}
            onSelectItem={handleSelectItem}
            isFiltered={isFiltered}
            isClosed={isClosed}
          />
        </div>
      </div>

      {selectedItemId && (
        <>
          <MenuItemDetailDialog
            selectedItemId={selectedItemId}
            setSelectedItemId={setSelectedItemId}
            selectedItem={selectedItem ?? null}
            restaurantId={restaurantId}
            isRestoClosed={isClosed}
          />
        </>
      )}
    </section>
  )
}

MenuCategories.displayName = "MenuCategories"

export { MenuCategories }
